import ApiService from "../../../core/services/ApiService";
import { normalizeTaxYear } from "../../../shared/utils/NormalizeTaxYear";

export interface ProjectDto {
  id: number;
  name: string;
  tenantName: string;
  engagementId: number;
  engagementName: string;
  entityId: number;
  entityName: string;
  taxYear: number;
  engagementType: string;
  allocationMethod: string;
  status: string;
  draftVersion: number;
  createdBy: string;
  createdUserName: string;
  dateCreated: string;
}

export interface ProjectCreateDto {
  name: string;
  entityId: number;
  taxYear: number;
  engagementType: string;
  allocationMethod?: string;
  calcProfileId?: string;
}

export interface CalcProfileDto {
  id: string;
}

export interface ProjectListFilter {
  itemsPerPage?: number;
  startIndex?: number;
  searchValue?: string;
  entityId?: number;
  taxYear?: number;
  engagementType?: string;
}

export interface EntityDropdownDto {
  id: number;
  name: string;
  orgId: string;
}

export interface EngagementDropdownDto {
  id: number;
  name: string;
  taxYear: number;
}

export interface DeliverableDto {
  id: number;
  name: string;
  ruleEngineId: number;
  coaId?: number;
  ruleEngineTemplateName?: string;
}

export interface TenantInfoDto {
  tenantId: string;
  tenantName: string;
}

export interface PagedResponse<T> {
  totalCount: number;
  items: T;
}

class ProjectService extends ApiService {
  async createProject(data: ProjectCreateDto): Promise<number> {
    return this.makeRequest<number>("/api/project/create", {
      method: "POST",
      data: data as any,
      extractData: true,
    });
  }

  async getProjects(
    filter: ProjectListFilter,
  ): Promise<PagedResponse<ProjectDto[]>> {
    return this.makeRequest<PagedResponse<ProjectDto[]>>("/api/project/list", {
      method: "POST",
      data: filter as any,
      extractData: true,
    });
  }

  async getProjectById(id: number): Promise<ProjectDto> {
    return this.makeRequest<ProjectDto>(`/api/project/${id}`, {
      extractData: true,
    });
  }

  async getProjectDeliverables(projectId: number): Promise<any[]> {
    return this.makeRequest<any[]>(`/api/project/${projectId}/deliverables`, {
      extractData: true,
    });
  }

  async getEntities(): Promise<EntityDropdownDto[]> {
    return this.makeRequest<EntityDropdownDto[]>(`/api/project/entities`, {
      extractData: true,
    });
  }

  async getEngagements(): Promise<EngagementDropdownDto[]> {
    return this.makeRequest<EngagementDropdownDto[]>(
      `/api/project/engagements`,
      {
        extractData: true,
      },
    );
  }

  async assignDeliverableToProject(
    projectId: number,
    deliverableId: number,
  ): Promise<void> {
    return this.makeRequest<void>(
      `/api/project/${projectId}/assign-deliverable`,
      {
        method: "PUT",
        data: { deliverableId } as any,
        extractData: true,
      },
    );
  }

  async getUnassignedDeliverables(): Promise<DeliverableDto[]> {
    return this.makeRequest<DeliverableDto[]>(`/api/deliverable/unassigned`, {
      extractData: true,
    });
  }

  async getTenantInfo(): Promise<TenantInfoDto> {
    return this.makeRequest<TenantInfoDto>(`/api/tenant/info`, {
      extractData: true,
    });
  }

  async getCalcProfile(partnershipId: number, taxYear: string): Promise<CalcProfileDto> {
    return this.makeRequest<CalcProfileDto>('/api/calc-profile', {
      params: { partnershipId, taxYear: normalizeTaxYear(taxYear) },
      extractData: true,
    });
  }
}

export default ProjectService;
